import moment from 'moment'
import * as React from 'react'
import I18n from '../../../../core/i18n'
import LinkButton from 'components/atoms/LinkButton'
import { directReservationService, reservationService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button } from '../../../atoms'
import { showDeclineModal } from './DeclineModal'
import { showCancelApplicationModal } from './CancelApplicationModal'

interface IProps {
  currentRecord: any
  user: interfaces.IUser
  hostCancelReasons: any
  currentUserIsAgent: boolean
  setRecords: (record: any) => void
}

declare var window: interfaces.IWindow

const getRemainDaysFlg = tourDate => {
  return (
    Number(
      moment()
        .add(4, 'days')
        .format('YYYYMMDD')
    ) > Number(moment(tourDate).format('YYYYMMDD'))
  )
}
const getDetailPath = tour_reservation => {
  return tour_reservation.reservable_type === 'Reservation'
    ? `/reservations/${tour_reservation.reservable.slug}`
    : `/agent/direct_reservations/${tour_reservation.reservable.slug}`
}

const isAssignedReservation = tour_reservation => {
  return (
    tour_reservation.tour_group?.guide &&
    tour_reservation.tour_group.guide.id !== tour_reservation.reservable.post.user_id
  )
}

export const HostWorkflowOperations: React.FC<IProps> = ({
  currentRecord,
  user,
  hostCancelReasons,
  currentUserIsAgent,
  setRecords,
}) => {
  const formRef = React.useRef(null)

  const isPastReservation = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('time_scope') === 'past'
  }, [])

  const guestIsNonMember = currentRecord.reservable.user?.non_member
  const canShowNonMemberAccessUrlCopy =
    currentUserIsAgent && currentRecord.reservable_type === 'Reservation' && !isPastReservation

  const showMessageButton = record => {
    return isAssignedReservation(record) || !guestIsNonMember
  }

  const deleteDirectReservation = React.useCallback(async directReservation => {
    const { flush } = await directReservationService.deleteDirectReservation(directReservation.id)

    updateRecordsAfterDestroy(directReservation)
    window.flashMessages.addMessage({
      text: flush.message,
      type: flush.type,
    })
  }, [])

  const updateRecordsAfterDestroy = record => {
    setRecords(old =>
      old.filter(re => re.reservable_type !== 'DirectReservation' || re.reservable.id !== record.id)
    )
  }

  const isAvailableTourGroupMessage = React.useCallback(tour_reservation => {
    return (
      getRemainDaysFlg(tour_reservation.reservable.start_at) &&
      tour_reservation.tour_group?.conversation &&
      tour_reservation.tour_group?.conversation.available
    )
  }, [])

  const getMessagePath = tour_reservation => {
    if (isAssignedReservation(tour_reservation)) {
      const counterpart_id = user.guide
        ? tour_reservation.reservable.post.user_id
        : tour_reservation.tour_group?.guide?.id
      return isAvailableTourGroupMessage(tour_reservation)
        ? `/conversations/${tour_reservation.tour_group.conversation.slug}`
        : `/conversations/agent_guide?counterpart_id=${counterpart_id}`
    } else {
      return tour_reservation.reservable_type === 'Reservation'
        ? `/conversations/${tour_reservation.reservable.conversation?.slug}`
        : ''
    }
  }

  // ワークフロー更新APIのレスポンスは ReservationSerializer のため、一覧用の ReservableSerializer にしかない
  // start_at_raw が含まれず、end_at の書式も異なる。催行日時はワークフロー変更で変わらないので既存値を維持する。
  const updateRecordsAfterUpdateWorkflowState = updatedRecord => {
    setRecords(old =>
      old.map(record =>
        record.reservable_type !== 'DirectReservation' && record.reservable_id === updatedRecord.id
          ? {
              ...record,
              reservable: {
                ...record.reservable,
                ...updatedRecord,
                start_at: record.reservable.start_at,
                start_at_raw: record.reservable.start_at_raw,
                end_at: record.reservable.end_at,
              },
            }
          : { ...record }
      )
    )
  }

  const copyNonMemberReservationUrl = React.useCallback(async tour_reservation => {
    try {
      const reservationId = tour_reservation?.reservable?.id
      const { accessUrl } = await reservationService.getNonMemberAccessUrl(reservationId)
      await navigator.clipboard.writeText(accessUrl)
      window.flashMessages.addMessage({ text: I18n.t('generic.copied'), type: 'success' })
    } catch (error) {
      window.flashMessages.addMessage({ text: 'コピーに失敗しました', type: 'error' })
    }
  }, [])

  const approveReservation = React.useCallback(async id => {
    try {
      const {
        reservation: changedReservation,
        flush,
      } = await reservationService.approveReservation(id)
      updateRecordsAfterUpdateWorkflowState(changedReservation)
      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
    } catch (error) {
      console.error(error)
    }
  }, [])

  const handleDeclineReservation = React.useCallback((id, reservation, flush) => {
    updateRecordsAfterUpdateWorkflowState(reservation)
    window.flashMessages.addMessage({
      text: flush.message,
      type: flush.type,
    })
  }, [])

  // 仮売上状態（キャプチャ前）のインスタントブッキングを仮売上キャンセルで即時キャンセルする
  const cancelCapturePendingReservation = React.useCallback(async reservation => {
    if (!window.confirm('この予約をキャンセルし、仮売上を取り消します。よろしいですか？')) {
      return
    }
    try {
      const {
        reservation: canceledReservation,
        flush,
      } = await reservationService.cancelReservation(reservation.id, {
        cancel_fee: 0,
        status: reservation.workflow_state_enum_value,
      })
      updateRecordsAfterUpdateWorkflowState(canceledReservation)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (error) {
      console.error(error)
    }
  }, [])

  const repayReservation = React.useCallback(async id => {
    if (!window.confirm('この予約を再決済します。よろしいですか？')) {
      return
    }
    try {
      const {
        reservation: changedReservation,
        flush,
        client_secret,
      } = await reservationService.repayReservation(id)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.reload()
    } catch (error) {
      console.error(error)
    }
  }, [])

  const renderWorkFlowStateButtons = React.useCallback(
    tour_reservation => {
      if (
        !tour_reservation.reservable.next_work_flow_state ||
        (user.guide && isAssignedReservation(tour_reservation))
      ) {
        return null
      }

      return (
        <div>
          {tour_reservation.reservable.next_work_flow_state.map((state, index) => {
            const reservation = tour_reservation.reservable
            switch (state) {
              case 'approved':
                return (
                  <Button
                    key={`${reservation.id}-${index}`}
                    small={true}
                    rounded={true}
                    textColor={constants.COLORS.success}
                    borderColor={constants.COLORS.success}
                    handleClick={() => approveReservation(reservation.id)}
                  >
                    {I18n.t('generic.approve')}
                  </Button>
                )
              case 'declined':
                return (
                  <Button
                    key={`${reservation.id}-${index}`}
                    small={true}
                    rounded={true}
                    textColor={constants.COLORS.caution}
                    borderColor={constants.COLORS.caution}
                    handleClick={() => {
                      showDeclineModal(
                        user,
                        reservation.id,
                        handleDeclineReservation,
                        formRef,
                        hostCancelReasons
                      )
                    }}
                  >
                    {I18n.t('generic.decline')}
                  </Button>
                )
              case 'canceled':
                return tour_reservation.cancel_application &&
                  tour_reservation.cancel_application.status === 'requested' ? (
                  <a href={`/agent/cancel_applications`}>
                    <Button
                      small={true}
                      rounded={true}
                      textColor={constants.COLORS.caution}
                      borderColor={constants.COLORS.caution}
                    >
                      {I18n.t('reservation.cancel_application')}
                    </Button>
                  </a>
                ) : (
                  <Button
                    key={`${reservation.id}-${index}`}
                    small={true}
                    rounded={true}
                    textColor={constants.COLORS.caution}
                    borderColor={constants.COLORS.caution}
                    handleClick={() => {
                      if (currentUserIsAgent) {
                        // 仮売上状態（キャプチャ前）のインスタントブッキングは、キャンセル申請ではなく
                        // 仮売上キャンセルで即時キャンセルする
                        reservation.capture_pending
                          ? cancelCapturePendingReservation(reservation)
                          : showCancelApplicationModal(tour_reservation, formRef)
                      } else {
                        showDeclineModal(
                          user,
                          reservation.id,
                          handleDeclineReservation,
                          formRef,
                          hostCancelReasons
                        )
                      }
                    }}
                  >
                    {I18n.t('generic.cancel')}
                  </Button>
                )
              case 'repay':
                return (
                  <Button
                    small={true}
                    rounded={true}
                    textColor={constants.COLORS.success}
                    borderColor={constants.COLORS.success}
                    handleClick={() => repayReservation(reservation.id)}
                  >
                    {I18n.t('generic.repay')}
                  </Button>
                )
              default:
                return null
            }
          })}
        </div>
      )
    },
    [user, formRef, hostCancelReasons]
  )

  return (
    <>
      <div style={{ display: 'flex', gap: '8px' }}>
        {isPastReservation
          ? currentRecord.reservable.workflow_state_enum_value !== 'declined' &&
            currentRecord.reservable.workflow_state_enum_value !== 'requested' && (
              <>
                {showMessageButton(currentRecord) && (
                  <LinkButton
                    miniLink={true}
                    showBorder={true}
                    href={getMessagePath(currentRecord)}
                  >
                    {I18n.t('generic.message')}
                  </LinkButton>
                )}

                <Button
                  rounded={true}
                  backgroundColor={
                    currentRecord.reservable.thanks_mail_sent_count > 0
                      ? constants.COLORS.success
                      : constants.COLORS.caution
                  }
                  textColor={constants.COLORS.white}
                  small={true}
                >
                  {currentRecord.reservable_type === 'DirectReservation' ? (
                    <a
                      href={`/agent/direct_reservations/${currentRecord.reservable.id}/edit_thanks_mails`}
                    >
                      Thanks Mail
                    </a>
                  ) : (
                    <a
                      href={`/agent/reservations/${currentRecord.reservable.id}/edit_thanks_mails`}
                    >
                      Thanks Mail
                    </a>
                  )}
                </Button>
              </>
            )
          : currentRecord.reservable_type === 'Reservation' && (
              <>
                {showMessageButton(currentRecord) && (
                  <LinkButton
                    miniLink={true}
                    showBorder={true}
                    href={getMessagePath(currentRecord)}
                  >
                    {I18n.t('generic.message')}
                  </LinkButton>
                )}
                {renderWorkFlowStateButtons(currentRecord)}
              </>
            )}
        {currentRecord.reservable_type === 'DirectReservation' && !isPastReservation && (
          <>
            {isAssignedReservation(currentRecord) ? (
              <Button small={true} rounded={true}>
                <a href={getMessagePath(currentRecord)}>{I18n.t('generic.message')}</a>
              </Button>
            ) : (
              <Button
                small={true}
                rounded={true}
                handleClick={() => {
                  if (window.confirm(I18n.t('generic.delete_confirmation'))) {
                    deleteDirectReservation(currentRecord.reservable)
                  }
                }}
              >
                {I18n.t('generic.delete')}
              </Button>
            )}
          </>
        )}
        <LinkButton href={getDetailPath(currentRecord)} miniLink={true} showBorder={true}>
          {I18n.t('generic.show_detail')}
        </LinkButton>
        {canShowNonMemberAccessUrlCopy && (
          <Button
            small={true}
            rounded={true}
            handleClick={() => copyNonMemberReservationUrl(currentRecord)}
          >
            {I18n.t('post.copy_url')}
          </Button>
        )}
      </div>
    </>
  )
}
